import { useState } from "react";
import { toast } from "sonner";
import { rd, api, printThermal, printA4, paymentLabel, waUrl, qrDataUrl } from "./posShared";
import { Printer, FileText, MessageCircle, Mail, X, Check } from "lucide-react";

export default function InvoicePrint({ invoice, settings, customers, onClose }) {
  const [emailing, setEmailing] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState((invoice && (invoice.customer_email || "")) || "");
  if (!invoice) return null;
  const s = settings || {};
  const isQuote = invoice.doc_type === "cotizacion";
  const docWord = isQuote ? "Cotización" : "Factura";
  const emailBase = isQuote ? "quotes" : "invoices";

  const cust = (customers || []).find((c) => c.id === invoice.customer_id);
  const custNumber = (invoice.customer_whatsapp || invoice.customer_phone || cust?.whatsapp || cust?.phone || "").replace(/\D/g, "");

  const buildMessage = () => {
    let m = `*${s.business_name || "MO by Vir"}*\n`;
    m += `${docWord} ${invoice.number}${invoice.ncf ? ` · NCF ${invoice.ncf}` : ""}\n`;
    m += `Cliente: ${invoice.customer_name}\n\n`;
    invoice.items.forEach((i) => { m += `${i.qty} × ${i.name} — ${rd(i.price * i.qty)}\n`; });
    m += `\nSubtotal: ${rd(invoice.subtotal)}\n`;
    if (invoice.discount) m += `Descuento: -${rd(invoice.discount)}\n`;
    if (invoice.itbis) m += `ITBIS: ${rd(invoice.itbis)}\n`;
    m += `*Total: ${rd(invoice.total)}*\n`;
    if (!isQuote) m += `Pago: ${paymentLabel(invoice.payment_method)}\n`;
    if (isQuote && invoice.valid_until) m += `Válida hasta: ${invoice.valid_until}\n`;
    m += `\n${s.thank_you_message || "¡Gracias por su compra!"}`;
    return m;
  };

  // Envía la factura directamente al WhatsApp del cliente (o al negocio si no hay número).
  const shareWhatsApp = () => {
    const num = custNumber || (s.whatsapp || "").replace(/\D/g, "");
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(buildMessage())}`, "_blank");
  };

  const sendEmail = async () => {
    const to = (emailTo || "").trim();
    if (!to || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      toast.error("Ingresa un correo válido");
      return;
    }
    setEmailing(true);
    try {
      await api.post(`/pos/${emailBase}/${invoice.id}/email`, { to });
      toast.success(`${docWord} enviada a ${to}`);
      setEmailOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo enviar el correo");
    } finally { setEmailing(false); }
  };

  return (
    <div className="fixed inset-0 z-[95] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100 sticky top-0 bg-white">
          <span className="font-medium">{docWord} {invoice.number}</span>
          <button data-testid="invoice-close" onClick={onClose} className="text-neutral-400 hover:text-espresso"><X size={20} /></button>
        </div>

        {(invoice._sentEmail || invoice._sentWhatsApp) && (
          <div data-testid="invoice-sent-badge" className="flex flex-wrap items-center gap-2 px-5 py-2.5 bg-emerald-50 border-b border-emerald-100">
            {invoice._sentEmail && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                <Check size={14} /> Enviado por correo a {invoice._sentEmail}
              </span>
            )}
            {invoice._sentWhatsApp && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                <Check size={14} /> WhatsApp abierto ({invoice._sentWhatsApp})
              </span>
            )}
          </div>
        )}

        <div id="invoice-print-area" className="p-6 text-espresso text-sm">
          <div className="text-center">
            {s.logo && <img src={s.logo} alt="logo" className="h-14 mx-auto object-contain mb-2" />}
            <h2 className="font-serif text-2xl">{s.business_name || "MO by Vir"}</h2>
            {s.rnc && <p className="text-xs">RNC: {s.rnc}</p>}
            {s.address && <p className="text-xs text-neutral-500">{s.address}</p>}
            {s.phone && <p className="text-xs text-neutral-500">Tel: {s.phone}</p>}
          </div>

          <div className="flex justify-between text-xs mt-4 pt-3 border-t border-dashed border-neutral-300">
            <div>
              <p><strong>{docWord}:</strong> {invoice.number}</p>
              {invoice.ncf && <p><strong>NCF:</strong> {invoice.ncf}</p>}
              <p><strong>Fecha:</strong> {(invoice.created_at || "").slice(0, 16).replace("T", " ")}</p>
              {invoice.valid_until && <p><strong>Válida hasta:</strong> {invoice.valid_until}</p>}
            </div>
            <div className="text-right">
              <p><strong>Cliente:</strong></p>
              <p>{invoice.customer_name}</p>
              {invoice.customer_address && <p className="text-neutral-500 max-w-[180px]">{invoice.customer_address}</p>}
            </div>
          </div>

          <table className="w-full text-sm mt-3">
            <thead><tr className="border-b border-neutral-300 text-left text-espresso"><th className="py-1.5">Producto</th><th className="text-center">Cant</th><th className="text-right">Precio</th><th className="text-right">Total</th></tr></thead>
            <tbody>
              {invoice.items.map((i, idx) => (
                <tr key={idx} className="border-b border-neutral-100">
                  <td className="py-1.5">{i.name}</td>
                  <td className="text-center">{i.qty}</td>
                  <td className="text-right">{rd(i.price)}</td>
                  <td className="text-right">{rd(i.price * i.qty)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4 text-sm space-y-1">
            <L l="Subtotal" v={rd(invoice.subtotal)} strong />
            {invoice.discount > 0 && <L l="Descuento" v={`- ${rd(invoice.discount)}`} />}
            {invoice.itbis > 0 && <L l={`ITBIS ${invoice.itbis_rate}%`} v={rd(invoice.itbis)} />}
            {invoice.delivery_fee > 0 && <L l={`Delivery${invoice.delivery_label ? ` · ${invoice.delivery_label}` : ""}`} v={rd(invoice.delivery_fee)} />}
            <div className="flex justify-between font-bold text-lg pt-2 border-t border-neutral-300"><span>TOTAL</span><span>{rd(invoice.total)}</span></div>
            {!isQuote && <L l="Método de pago" v={paymentLabel(invoice.payment_method)} />}
            {!isQuote && invoice.change > 0 && <L l="Cambio" v={rd(invoice.change)} strong />}
            {!isQuote && invoice.balance > 0 && <L l="Balance pendiente" v={rd(invoice.balance)} strong />}
          </div>

          <p className="text-xs text-neutral-500 mt-4">Vendedor: {invoice.seller}</p>
          <p className="text-center text-sm mt-3">{s.thank_you_message || "¡Gracias por su compra!"}</p>
          {waUrl(s) && (
            <div data-testid="invoice-wa-cta" className="mt-4 pt-4 border-t border-dashed border-neutral-200 flex flex-col items-center">
              <img src={qrDataUrl(waUrl(s))} alt="WhatsApp QR" className="w-28 h-28" />
              <a href={waUrl(s)} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-[#25D366] hover:underline">
                Escríbenos por WhatsApp
              </a>
              <span className="text-xs text-neutral-500">{s.whatsapp || s.phone}</span>
            </div>
          )}
          {invoice.status === "anulada" && <p className="text-center text-red-500 font-bold mt-2">*** ANULADA ***</p>}
        </div>

        <div className="p-4 border-t border-neutral-100 space-y-2 sticky bottom-0 bg-white">
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="invoice-print-thermal" onClick={() => printThermal(invoice, s)} className="inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full py-3 text-xs uppercase tracking-[0.1em] hover:bg-rose transition"><Printer size={14} /> Ticket 80mm</button>
            <button data-testid="invoice-print-a4" onClick={() => printA4(invoice, s)} className="inline-flex items-center justify-center gap-2 border border-espresso text-espresso rounded-full py-3 text-xs uppercase tracking-[0.1em] hover:bg-espresso hover:text-cream transition"><FileText size={14} /> {docWord} A4 / PDF</button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="invoice-whatsapp" onClick={shareWhatsApp} className="inline-flex items-center justify-center gap-2 bg-rose text-cream rounded-full py-3 text-xs uppercase tracking-[0.1em] hover:bg-rose-hover transition"><MessageCircle size={14} /> {custNumber ? "WhatsApp al cliente" : "WhatsApp"}</button>
            <button data-testid="invoice-email" onClick={() => setEmailOpen(true)} disabled={emailing} className="inline-flex items-center justify-center gap-2 border border-rose text-rose rounded-full py-3 text-xs uppercase tracking-[0.1em] hover:bg-rose hover:text-cream transition disabled:opacity-60"><Mail size={14} /> {emailing ? "Enviando..." : "Correo"}</button>
          </div>
          {custNumber ? (
            <p data-testid="invoice-whatsapp-hint" className="text-[11px] text-center text-neutral-400">Se abrirá WhatsApp con el número del cliente: {custNumber}</p>
          ) : (
            <p data-testid="invoice-whatsapp-hint" className="text-[11px] text-center text-amber-600">El cliente no tiene WhatsApp/teléfono registrado. Se abrirá el WhatsApp del negocio.</p>
          )}
        </div>
      </div>

      {emailOpen && (
        <div data-testid="invoice-email-modal" className="fixed inset-0 z-[97] bg-black/50 flex items-center justify-center p-4" onClick={() => !emailing && setEmailOpen(false)}>
          <div className="bg-white w-full max-w-sm rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1"><Mail size={18} className="text-rose" /><h3 className="font-serif text-xl">Enviar {docWord.toLowerCase()} por correo</h3></div>
            <p className="text-xs text-neutral-500 mb-4">{docWord} {invoice.number} · se enviará el detalle al correo indicado.</p>
            <label className="text-[11px] uppercase tracking-[0.12em] text-neutral-500">Correo del cliente</label>
            <input data-testid="invoice-email-input" type="email" autoFocus value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") sendEmail(); }}
              placeholder="cliente@correo.com"
              className="w-full mt-1 bg-white border border-neutral-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-rose focus:ring-1 focus:ring-rose/30 transition" />
            <div className="grid grid-cols-2 gap-2 mt-5">
              <button data-testid="invoice-email-cancel" onClick={() => setEmailOpen(false)} disabled={emailing} className="rounded-full py-2.5 text-xs uppercase tracking-[0.1em] border border-neutral-200 text-neutral-600 hover:border-espresso transition disabled:opacity-60">Cancelar</button>
              <button data-testid="invoice-email-send" onClick={sendEmail} disabled={emailing} className="rounded-full py-2.5 text-xs uppercase tracking-[0.1em] bg-rose text-cream hover:bg-rose-hover transition disabled:opacity-60">{emailing ? "Enviando..." : "Enviar"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const L = ({ l, v, strong }) => (
  <div className={`flex justify-between capitalize ${strong ? "font-bold text-espresso text-[15px]" : ""}`}>
    <span className={strong ? "" : "text-neutral-600"}>{l}</span><span>{v}</span>
  </div>
);
